import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import TextFieldComponent from '../../../components/TextFieldComponent';

import zod from 'zod';
import { ResponsibleFormSchema } from '../../../services/ResponsibleService/schemas';
import { Grid2 } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import ButtonComponent from '../../../components/ButtonComponent';

import { AppContext } from '../../../context/AppContextProvider';
import ModalComponent from '../../../components/ModalComponent';

import DropdownSelectComponent from '../../../components/DropdownSelectComponent';
import { useGetAllStudentByLicenseMutateAsync } from '../../../services/StudentService/hooks/useMutation';
import { IStudentSimpleProps } from '../../../services/StudentService/types';

import {
  useCreateSchoolClassAsync,
  useUpdateSchoolClassAsync,
} from '../../../services/SchoolClass/hooks/useMutation';
import { SchoolClassFormSchema } from '../../../services/SchoolClass/schemas';
import { useGetAllStudentsByLicenseMutateAsync } from '../../../services/Recognition/Student/hooks/useMutation';
import { IStudentRecognitionSimpleProps } from '../../../services/Recognition/Student/types';
import { ISchoolShiftSimpleProps } from '../../../services/SchoolShift/types';
import DropdownComponent from '../../../components/DropdownComponent';
import { useGetAllSchoolShiftByLicenseAsync } from '../../../services/SchoolShift/hooks/useMutation';
import { useGetAllRecognitionsByLicenseAsync } from '../../../services/Recognition/hooks/useMutation';
import { IRecognitionSimpleProps } from '../../../services/Recognition/types';

export type SchoolClassFormInput = zod.infer<typeof SchoolClassFormSchema>;

interface SchoolClassEditionModalProps {
  isOpen: boolean;
  responsibleEdit?: any;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  handleSaveForm?: () => void;
  handleClose?: () => void;
}

const SchoolClassEditionModal = ({
  isOpen,
  setIsOpen,
  responsibleEdit,
  handleClose,
  handleSaveForm,
}: SchoolClassEditionModalProps) => {
  const [isDeviceSelected, setIsDeviceSelected] = useState<boolean>(false);
  const form = useForm<SchoolClassFormInput>({
    resolver: zodResolver(SchoolClassFormSchema),
    defaultValues: {
      recognitionsId: [],
      name: '',
      shiftId: null,
    },
  });
  const {
    control,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;
  const { showSnackbar } = useContext(AppContext);

  const [students, setStudents] = useState<IRecognitionSimpleProps[]>([]);
  const [shiftOptions, setShiftOptions] = useState<ISchoolShiftSimpleProps[]>(
    [],
  );
  const {
    mutate: mutateCreateSchoolClass,
    isLoading: isLoadingCreateResponsible,
  } = useCreateSchoolClassAsync();
  const { mutateAsync: mutateGetAllStudentByLicenseAsync } =
    useGetAllRecognitionsByLicenseAsync();
  const { mutate: mutateUpdate, isLoading: isLoadingUpdateResponsible } =
    useUpdateSchoolClassAsync();
  const { mutateAsync: mutateGetShift, isLoading: isLoadingGetShift } =
    useGetAllSchoolShiftByLicenseAsync();
  useEffect(() => {
    mutateGetShift().then(setShiftOptions);
  }, []);
  useEffect(() => {
    if (responsibleEdit) {
      reset(responsibleEdit, { keepDefaultValues: true });
    }
  }, [responsibleEdit]);

  const handleSave = (data: any) => {
    if (data.id) {
      mutateUpdate(
        {
          data,
          id: data.id,
        },
        {
          onSuccess: () => {
            resetAllAndShowMessage(
              'Transporte escolar atualizado com sucesso.',
            );
          },
        },
      );
      return;
    }

    mutateCreateSchoolClass(
      {
        data,
      },
      {
        onSuccess: () => {
          resetAllAndShowMessage('Transporte escolar cadatrado com sucesso.');
        },
        onError() {
          showSnackbar('Erro ao cadastrar Transporte escolar.', 'error');
        },
      },
    );
  };

  const resetAllAndShowMessage = (message: string) => {
    if (handleSaveForm) handleSaveForm();
    showSnackbar(message, 'success');
    reset();
    setIsOpen(false);
  };

  const onCloseModal = () => {
    setIsOpen(false);
    if (handleClose) handleClose();
    reset();
  };

  useEffect(() => {
    mutateGetAllStudentByLicenseAsync(undefined).then(setStudents);
  }, []);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onCloseModal}
      title={'Edição de Turma'}
    >
      <Grid2
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{ mt: 2 }}
      >
        <Grid2 size={isDeviceSelected ? 12 : 6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Nome"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DropdownSelectComponent
                label="Usuarios"
                selectedValues={value as any[]}
                onSelectedValues={onChange}
                options={students}
                labelField={'name'}
                valueField={'id'}
              />
            )}
            name={'recognitionsId'}
            control={control}
          />
          <Controller
            render={({
              field: { value, onChange },
              fieldState: { error, invalid },
            }) => (
              <DropdownComponent
                options={shiftOptions}
                label="Turno"
                optionLabel={'name'}
                optionValue={'id'}
                errorMessage={error?.message}
                value={value}
                onChange={onChange}
              />
            )}
            name={'shiftId'}
            control={control}
          />
        </Grid2>
      </Grid2>
      <ButtonComponent
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit(handleSave)}
        loading={isLoadingCreateResponsible || isLoadingUpdateResponsible}
      >
        Salvar
      </ButtonComponent>
    </ModalComponent>
  );
};

export default SchoolClassEditionModal;
